import "./overlaps.css";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildConstraintSeqIds } from "../../utils/utils";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import { Form } from "react-bootstrap";
import Offcanvas from "react-bootstrap/Offcanvas";
import Spinner from "react-bootstrap/Spinner";
import { fetchTreeLeaves } from "../../utils/phyloFetch";
import { patientActions } from "../../actions/PatientActions";
import { useApi } from "../../contexts/ApiProvider";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

export default function OverlapMenu({ adm, seqIds, indexSeqId, survey_mode, debug_mode, debug_survey_mode}) {
  const api = useApi();

  const dispatch = useDispatch();
  var micro_id = useSelector((state) => state.patient.micro_id);
  var tree_exists = useSelector((state) => state.patient.tree.tree);
  const overlap_constraint = useSelector((state) => state.patient.tree.overlap_constraint);
  const snp_dist = useSelector((state) => state.patient.snp_distance);


  const reduxSNPDistance = useSelector((state) => state.patient.snp_distance);
  const constraintLoading = useSelector((state) => state.patient.tree.constraint_loading);
  const reduxSelectedLevel = useSelector((state) => state.patient.patient.overlap_level);
  const reduxSelectedContam = useSelector((state) => state.patient.patient.contam_period);
  const reduxSelectedIncubation = useSelector((state) => state.patient.patient.incubation);
  const reduxSelectedInfPrior = useSelector((state) => state.patient.patient.inf_period_prior);
  const reduxSelectedInfAfter = useSelector((state) => state.patient.patient.inf_period_after);
  const reduxSelectedTTB = useSelector((state) => state.patient.patient.test_time_buffer);
  const reduxOverlapConstraint = useSelector((state) => state.patient.tree.overlap_constraint);

  const [isPhyloChecked, setIsPhyloChecked] = useState(reduxOverlapConstraint || false);
  const [snpDistance, setSnpDistance] = useState(reduxSNPDistance || "10");
  const [selectedLevel, setSelectedLevel] = useState(reduxSelectedLevel || "ward");
  const [selectedContam, setSelectedContam] = useState(reduxSelectedContam || "28");
  const [selectedIncubation, setSelectedIncubation] = useState(reduxSelectedIncubation || "84");
  const [selectedInfPrior, setSelectedInfPrior] = useState(reduxSelectedInfPrior || "0");
  const [selectedInfAfter, setSelectedInfAfter] = useState(reduxSelectedInfAfter || "56");
  const [selectedTTB, setSelectedTTB] =  useState(reduxSelectedTTB || "1");

  const appliedConstraintKeyRef = useRef(null);
  const overlapConstraintRef = useRef(overlap_constraint);
  const normalizedSeqIds = Array.isArray(seqIds) ? seqIds : [];
  const seqIdsKey = normalizedSeqIds.join(",");
  const constraintSeqIds = useMemo(
    () => buildConstraintSeqIds(normalizedSeqIds, indexSeqId),
    [seqIdsKey, indexSeqId]
  );
  const constraintSeqIdsKey = useMemo(
    () => (constraintSeqIds.length ? constraintSeqIds.join(",") : "__none__"),
    [constraintSeqIds]
  );
  const constraintKey = `${constraintSeqIdsKey}|${snp_dist}`;

  useEffect(() => {
    overlapConstraintRef.current = overlap_constraint;
  }, [overlap_constraint]);

  const handleOverlapConstraintToggle = async () => {
    const enabling = !overlap_constraint;
    dispatch(patientActions.constrainByOverlap(enabling));
    overlapConstraintRef.current = enabling;
    if (enabling) {
      if (constraintSeqIds.length === 0) {
        // nothing to constrain (no index sequence available)
        dispatch(patientActions.constrainByOverlap(false));
        overlapConstraintRef.current = false;
        return;
      }
      dispatch(patientActions.constraintLoadingStart());
      appliedConstraintKeyRef.current = constraintKey;
      dispatch(patientActions.constrainTree(constraintSeqIds));
      try {
        const constraints = await fetchTreeLeaves(api, micro_id, snp_dist);
        if (
          !overlapConstraintRef.current ||
          appliedConstraintKeyRef.current !== constraintKey
        ) {
          return;
        }
        dispatch(patientActions.constrainOverlap(constraints));
      } catch (error) {
        console.error("Error fetching overlap constraints:", error);
        if (
          overlapConstraintRef.current &&
          appliedConstraintKeyRef.current === constraintKey
        ) {
          dispatch(patientActions.constrainTree([]));
          dispatch(patientActions.constrainByOverlap(false));
          dispatch(patientActions.constrainOverlap([]));
          overlapConstraintRef.current = false;
          appliedConstraintKeyRef.current = null;
        }
      } finally {
        dispatch(patientActions.constraintLoadingEnd());
      }
    } else {
      dispatch(patientActions.constrainTree([]));
      dispatch(patientActions.constrainOverlap([]));
      appliedConstraintKeyRef.current = null;
    }
  };

  const handleLevelSelect = (evt) => {
    setSelectedLevel(evt);
    dispatch(patientActions.selectLevel(evt));
  };

  const handleContamSelect = (evt) => {
    setSelectedContam(evt);
    dispatch(patientActions.selectContamPeriod(evt));
  };

  const handleIncubationSelect = (evt) => {
    setSelectedIncubation(evt);
    dispatch(patientActions.selectIncubationPeriod(evt));
  };

  const handleInfPriorSelect = (evt) => {
    setSelectedInfPrior(evt);
    dispatch(patientActions.selectInfPeriodPrior(evt));
  };
  const handleInfAfterSelect = (evt) => {
    setSelectedInfAfter(evt);
    dispatch(patientActions.selectInfPeriodAfter(evt));
  };
  const handleTestTimeBufferSelect = (evt) => {
    setSelectedTTB(evt);
    dispatch(patientActions.selectTestTimeBuffer(evt));
  };

  const DEFAULTS = {
    level: "ward",
    contam: "28",
    incubation: "84",
    infAfter: "56",
    // infPrior: "0",
    // ttb: "1",
    overlapConstraint: false,
  };

  const handleResetAll = async () => {
    // local UI state
    setSelectedLevel(DEFAULTS.level);
    setSelectedContam(DEFAULTS.contam);
    setSelectedIncubation(DEFAULTS.incubation);
    // setSelectedInfPrior(DEFAULTS.infPrior);
    setSelectedInfAfter(DEFAULTS.infAfter);
    // setSelectedTTB(DEFAULTS.ttb);

    // redux state (drives the plot props via ptObj)
    dispatch(patientActions.selectLevel(DEFAULTS.level));
    dispatch(patientActions.selectContamPeriod(DEFAULTS.contam));
    dispatch(patientActions.selectIncubationPeriod(DEFAULTS.incubation));
    // dispatch(patientActions.selectInfPeriodPrior(DEFAULTS.infPrior));
    dispatch(patientActions.selectInfPeriodAfter(DEFAULTS.infAfter));
    // dispatch(patientActions.selectTestTimeBuffer(DEFAULTS.ttb));

    // also reset the “Link with Genetically Related Cases” toggle + constraints
    dispatch(patientActions.constrainByOverlap(DEFAULTS.overlapConstraint));
    dispatch(patientActions.constrainTree([]));
    dispatch(patientActions.constrainOverlap([]));
    appliedConstraintKeyRef.current = null;
    overlapConstraintRef.current = DEFAULTS.overlapConstraint;
  };

  const PARAM_COLOURS = {
    infected: "#d1615d",
    atRisk: "#5778a4",
    contam: "darkOrange",
  };

  const ParamDot = ({ color }) => (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 9,
        height: 9,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 8,
        marginBottom: 2,
        verticalAlign: "middle",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.1)", // subtle outline so it’s visible on any bg
      }}
    />
  );

  useEffect(() => {
    if (!overlap_constraint) {
      appliedConstraintKeyRef.current = null;
      return;
    }

    if (appliedConstraintKeyRef.current === constraintKey) return;

    if (constraintLoading) return;

    if (constraintSeqIds.length === 0) {
      dispatch(patientActions.constrainTree([]));
      dispatch(patientActions.constrainOverlap([]));
      appliedConstraintKeyRef.current = null;
      return;
    }

    let cancelled = false;
    const refreshConstraints = async () => {
      dispatch(patientActions.constraintLoadingStart());
      appliedConstraintKeyRef.current = constraintKey;
      dispatch(patientActions.constrainTree(constraintSeqIds));
      try {
        const constraints = await fetchTreeLeaves(api, micro_id, snp_dist);
        if (
          !cancelled &&
          overlapConstraintRef.current &&
          appliedConstraintKeyRef.current === constraintKey
        ) {
          dispatch(patientActions.constrainOverlap(constraints));
        }
      } catch (err) {
        if (!cancelled && appliedConstraintKeyRef.current === constraintKey) {
          console.error("Error updating overlap constraints:", err);
          dispatch(patientActions.constrainTree([]));
          dispatch(patientActions.constrainByOverlap(false));
          dispatch(patientActions.constrainOverlap([]));
          overlapConstraintRef.current = false;
          appliedConstraintKeyRef.current = null;
        }
      } finally {
        dispatch(patientActions.constraintLoadingEnd());
      }
    };

    refreshConstraints();

    return () => {
      cancelled = true;
    };
  }, [
    constraintKey,
    constraintSeqIds,
    snp_dist,
    overlap_constraint,
    constraintLoading,
    dispatch,
    api,
    micro_id,
  ]);

  return (
    <Container
      fluid
      className="d-flex flex-row flex-wrap justify-content-start "
    >
      {" "}
      <div className="flex-grow-1 text-left">
        <Dropdown as={ButtonGroup} onSelect={handleLevelSelect}>
          <Dropdown.Toggle className="blue-button" size="sm" as={Button}>
            <span style={{ fontWeight: 550 }}>Overlap Level</span>&nbsp;&nbsp;
            {selectedLevel[0].toUpperCase() + selectedLevel.slice(1)}
          </Dropdown.Toggle>
          <Dropdown.Menu id="level-menu" title="Overlap level" variant="light" size="sm">
            {[
              { key: "ward", label: "Ward" },
              { key: "specialty", label: "Specialty" },
              { key: "hospital", label: "Hospital" },
            ].map(({ key, label }) => (
              <Dropdown.Item
                key={key}
                className="custom-dropdown-item"
                eventKey={key}
                active={selectedLevel === key}
              >
                {label}{key === DEFAULTS.level ? " (default)" : ""}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="flex-grow-1 text-left">
        <Dropdown as={ButtonGroup} onSelect={handleIncubationSelect}>
          <Dropdown.Toggle size="sm" className="blue-button">
            <ParamDot color={PARAM_COLOURS.atRisk} />
            <span style={{ fontWeight: 550 }}>Incubation Period</span>&nbsp;&nbsp;
            {selectedIncubation} days
          </Dropdown.Toggle>
          <Dropdown.Menu id="incubation-menu" title="Incubation period" variant="light" size="sm">
            {["0","7","14","21","28","35","42","49","56","63","70","77","84","91", "100", "150", "200", "365"].map(period => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedIncubation.toString() === period}
                className="custom-dropdown-item"
              >
                {period}{period === DEFAULTS.incubation ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {/* <div className="flex-grow-1 text-left" >
        <Dropdown as={ButtonGroup} onSelect={handleInfPriorSelect}>
          <Dropdown.Toggle
            size="sm"
            className="blue-button"
            disabled={adm}
            id="dropdown-custom-components"
          >
            <span style={{ fontWeight: 550 }}>
              Infectious Period (Pre-test)
            </span>{" "}
            &nbsp;{selectedInfPrior} days
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="infprior-menu"
            title="Inf Period Prior"
            variant="light"
            size="sm"
          >
            {["0", "1", "2", "3", "5", "7", "14", "28"].map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedInfPrior.toString() === period}
                className="custom-dropdown-item"
              >
                {period}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div> */}
      <div className="flex-grow-1 text-left">
        <Dropdown as={ButtonGroup} onSelect={handleInfAfterSelect}>
          <Dropdown.Toggle size="sm" className="blue-button">
            <ParamDot color={PARAM_COLOURS.infected} />
            <span style={{ fontWeight: 550 }}>Infectious Period</span>&nbsp;&nbsp;
            {selectedInfAfter} days
          </Dropdown.Toggle>
          <Dropdown.Menu id="infafter-menu" title="Inf Period After" variant="light" size="sm">
            {["0","7","14","21","28","35","42","49","56","63","70","77","84","91"].map(period => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedInfAfter.toString() === period}
                className="custom-dropdown-item"
              >
                {period}{period === DEFAULTS.infAfter ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {/* <div className="flex-grow-1 text-left">
        <Dropdown as={ButtonGroup} onSelect={handleTestTimeBufferSelect}>
          <Dropdown.Toggle
            size="sm"
            className="blue-button"
            disabled={adm}
            id="dropdown-custom-components"
          >
            <span style={{ fontWeight: 550 }}>Test Time Buffer</span> &nbsp;
            {selectedTTB} day(s)
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="TTB-menu"
            title="Test Time Buffer"
            variant="light"
            size="sm"
          >
            {["0", "1", "2", "5", "7"].map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedTTB.toString() === period}
                className="custom-dropdown-item"
              >
                {period}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div> */}
      <div className="flex-grow-1 text-left">
        <Dropdown as={ButtonGroup} onSelect={handleContamSelect}>
          <Dropdown.Toggle size="sm" className="blue-button">
            <ParamDot color={PARAM_COLOURS.contam} />
            <span style={{ fontWeight: 550 }}>Contamination Period</span>&nbsp;&nbsp;
            {selectedContam} days
          </Dropdown.Toggle>
          <Dropdown.Menu id="contam-menu" title="Contamination period" variant="light" size="sm">
            {["0","7","14","21","28","35","42","49","56","63","70","77","84","91"].map(period => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedContam.toString() === period}
                className="custom-dropdown-item"
              >
                {period}{period === DEFAULTS.contam ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {/* Only show this switch if survey_mode >= 3 */}
      {((!debug_mode && survey_mode >= 3) || (debug_mode && debug_survey_mode >= 3)) && (
        <div className="flex-grow-1 text-left">
          <Form.Check
            type="switch"
            id="custom-switch"
            className="custom-switch"
            label={
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "purple",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                Show Genetically Related Case(s) Only
                {constraintLoading && (
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    size="sm"
                    style={{ width: "1rem", height: "1rem" }}
                  />
                )}
              </span>
            }
            style={{ paddingTop: "4.5px", marginLeft: "8px" }}
            checked={overlap_constraint}
            disabled={
              tree_exists === undefined ||
              tree_exists.tree === null ||
              adm ||
              constraintLoading ||
              constraintSeqIds.length === 0
            }
            onChange={handleOverlapConstraintToggle}
          />
        </div>
      )}
      <div className="ms-auto">
        <Button
          size="sm"
          variant="outline-primary"
          className="blue-button-flipped"
          onClick={handleResetAll}
        >
          ↺ Reset to default
        </Button>
      </div>
    </Container>
  );
}
